// Where a PO ships: one of HHL's locations or a vendor's site (a CM holding consigned parts), with
// the address and contact printed on the PO, editable in place.
import { MapPin } from "lucide-react";
import { useState } from "react";
import { isEmptyAddress, locationAddress, locationLines } from "../../../../shared/address.ts";
import type { Location, Po } from "../../../../shared/db.gen.ts";
import { phoneText } from "../../../../shared/phone.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { AddressHints, addressFieldDefs, PasteAddress, phoneField } from "../../ui/AddressEditor.tsx";
import { CreateDialog } from "../../ui/CreateDialog.tsx";
import { Button, cx, Select } from "../../ui/controls.tsx";
import { type FieldDef, Fields } from "../../ui/RecordPanel.tsx";
import { vendorLabel } from "./lib.ts";

const ADD = "__add_site";
// Addresses and phones --- the site's address in its country's fields (ui/AddressEditor.tsx), its phone as its country
// writes it; the server tidies both (mutations/addresses.ts).
const addressFields = (shopify: boolean, country: string): FieldDef[] => [
  { key: "name", label: "Name", readOnly: shopify },
  ...addressFieldDefs(country, "address_"),
  { key: "contact_name", label: "Attn", placeholder: "Who receives it" },
  phoneField("contact_phone"),
  { key: "contact_email", label: "Email" },
];

/** Places POs can ship to, HHL's first. */
export function useShipToOptions(current?: string | null) {
  const locations = useRows("location");
  const vendors = useRows("vendor");
  const usable = locations
    .filter((l) => (l.kind === "hhl" && l.active && !l.ignored) || l.kind === "vendor_site" || l.id === current)
    .sort((a, b) => Number(a.kind !== "hhl") - Number(b.kind !== "hhl") || a.name.localeCompare(b.name));
  const label = (l: Location) => {
    const vendor = l.kind === "vendor_site" ? vendorLabel(vendors.find((v) => v.id === l.vendor_id)) : "";
    return vendor && !l.name.toLowerCase().includes(vendor.toLowerCase()) ? `${l.name} (${vendor})` : l.name;
  };
  return usable.map((l) => ({ value: l.id, label: label(l), location: l }));
}

export function ShipTo({ po, disabled }: { po: Po; disabled: boolean }) {
  const options = useShipToOptions(po.ship_to_location_id);
  const vendors = useRows("vendor");
  const addresses = useRows("vendor_address");
  const contacts = useRows("vendor_contact");
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const location = options.find((o) => o.value === po.ship_to_location_id)?.location;
  const siteVendor = location?.vendor_id ? vendors.find((v) => v.id === location.vendor_id) : undefined;
  const canEditAddress = can("location.update_address");

  const setShipTo = (id: string | null) => mutate("po.update", { id: po.id, patch: { ship_to_location_id: id } });

  return (
    <div className="flex rounded-md px-2 py-0.5">
      <div className="w-24 shrink-0 pt-1.5 text-muted">Ship to</div>
      <div className="min-w-0 flex-1">
        <Select
          disabled={disabled}
          value={po.ship_to_location_id ?? ""}
          options={[
            { value: "", label: "Not set" },
            ...options.map((o) => ({ value: o.value, label: o.label })),
            ...(can("location.create_site") ? [{ value: ADD, label: "Add a vendor site…" }] : []),
          ]}
          onChange={(e) => (e.target.value === ADD ? setAdding(true) : setShipTo(e.target.value || null))}
          className={cx("border-transparent bg-transparent", !disabled && "hover:border-line")}
        />
        {location && !editing && (
          <div className="flex items-start gap-2 px-2.5 pb-1 text-[12px] text-muted">
            <MapPin className="mt-0.5 size-3 shrink-0" />
            <div className="min-w-0 flex-1 whitespace-pre-wrap">
              {locationLines(location).join("\n") || <span className="text-warn">No address yet; add one so it prints on the PO.</span>}
              {isEmptyAddress(locationAddress(location)) && location.address && (
                <div className="text-warn">Its address is one block of text: Edit, then Paste an address to put it in its fields.</div>
              )}
              {[location.contact_name && `Attn: ${location.contact_name}`, phoneText(location.contact_phone), location.contact_email].filter(Boolean).length >
                0 && (
                <div>
                  {[location.contact_name && `Attn: ${location.contact_name}`, phoneText(location.contact_phone), location.contact_email]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
            </div>
            {canEditAddress && (
              <Button size="sm" variant="ghost" className="h-5 px-1" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        )}
        {location && editing && (
          <div className="mb-1 rounded-lg border border-line py-1">
            <Fields
              compact
              row={location}
              fields={addressFields(!!location.shopify_location_id, location.address_country || siteVendor?.country || "")}
              update="location.update_address"
            />
            <AddressHints address={locationAddress(location)} />
            {isEmptyAddress(locationAddress(location)) && location.address && (
              <div className="px-2 pt-1 text-[11px] whitespace-pre-wrap text-muted">Its old text: {location.address}</div>
            )}
            <div className="flex justify-end gap-1 px-2">
              <PasteAddress
                hint={location.address_country || siteVendor?.country}
                onApply={(a) =>
                  mutate("location.update_address", {
                    id: location.id,
                    patch: {
                      address_line1: a.line1,
                      address_line2: a.line2,
                      address_district: a.district,
                      address_city: a.city,
                      address_region: a.region,
                      address_postal_code: a.postal_code,
                      address_country: a.country,
                      address: "", // the old block of text is in its fields now
                    },
                  })
                }
              />
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
      {adding && (
        <CreateDialog
          title="Add a vendor site"
          fields={[
            {
              key: "vendor_id",
              label: "Vendor",
              type: "select",
              options: vendors.filter((v) => !v.archived).map((v) => ({ value: v.id, label: vendorLabel(v) })),
            },
            { key: "name", label: "Name", placeholder: "e.g. Tailhoo factory (Shenzhen)" },
          ]}
          initial={{ vendor_id: vendors.find((v) => v.kind !== "supplier" && !v.archived)?.id ?? po.vendor_id }}
          onClose={() => setAdding(false)}
          onSubmit={async (v) => {
            const vendor = vendors.find((x) => x.id === v.vendor_id);
            if (!vendor) return false;
            // Start from the vendor's factory (or other) address and the contact who gets POs.
            const order = ["factory", "ship_from", "billing", "other", "remit"];
            const address = addresses.filter((a) => a.vendor_id === vendor.id).sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind))[0];
            const contact = contacts.filter((c) => c.vendor_id === vendor.id).sort((a, b) => Number(b.is_po_recipient) - Number(a.is_po_recipient))[0];
            const id = crypto.randomUUID();
            const ok = await mutate("location.create_site", {
              id,
              kind: "vendor_site",
              vendor_id: vendor.id,
              name: v.name.trim() || vendorLabel(vendor),
              // Addresses --- the vendor's address in its fields
              ...(address
                ? {
                    address_line1: address.line1,
                    address_line2: address.line2,
                    address_district: address.district,
                    address_city: address.city,
                    address_region: address.region,
                    address_postal_code: address.postal_code,
                    address_country: address.country,
                  }
                : { address_country: vendor.country }),
              contact_name: contact?.name ?? "",
              contact_phone: contact?.phone ?? "",
              contact_email: contact?.email ?? "",
            });
            if (ok) {
              await setShipTo(id);
              setEditing(true);
            }
            return ok;
          }}
        />
      )}
    </div>
  );
}
